import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingTransactions = ({ pendingTransactions, onCancel }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return 'ArrowDownToLine';
      case 'withdrawal': return 'ArrowUpFromLine';
      case 'transfer': return 'ArrowLeftRight';
      default: return 'DollarSign';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deposit': return 'text-green-600 bg-green-50';
      case 'withdrawal': return 'text-red-600 bg-red-50';
      case 'transfer': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getEstimatedTime = (type) => {
    switch (type) {
      case 'deposit': return '1-3 business days';
      case 'withdrawal': return '2-5 business days';
      case 'transfer': return 'Instant';
      default: return 'Processing';
    }
  };

  const handleCancelClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    if (selectedTransaction) {
      onCancel(selectedTransaction?.id);
      setShowCancelModal(false);
      setSelectedTransaction(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Pending Transactions</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">{pendingTransactions?.length} pending</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        {pendingTransactions?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Transactions</h3>
            <p className="text-gray-600">All your transactions have been processed successfully.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingTransactions?.map((transaction) => (
              <div
                key={transaction?.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${getTransactionColor(transaction?.type)}`}>
                      <Icon name={getTransactionIcon(transaction?.type)} size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-gray-900 capitalize">{transaction?.type}</h3>
                        <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                          Processing
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{transaction?.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={14} />
                          <span>Submitted: {formatDate(transaction?.submittedAt)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Timer" size={14} />
                          <span>ETA: {getEstimatedTime(transaction?.type)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900 mb-2">
                      {formatCurrency(Math.abs(transaction?.amount), transaction?.currency)}
                    </div>
                    {transaction?.cancellable && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        iconPosition="left"
                        onClick={() => handleCancelClick(transaction)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Progress</span>
                    <span>{transaction?.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${transaction?.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Additional Details */}
                {transaction?.reference && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Reference ID:</span>
                      <span className="font-mono text-gray-900">{transaction?.reference}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cancel Transaction</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Transaction:</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {selectedTransaction?.type}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(Math.abs(selectedTransaction?.amount), selectedTransaction?.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reference:</span>
                  <span className="text-sm font-mono text-gray-900">{selectedTransaction?.reference}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to cancel this transaction? Any processing fees may not be refunded.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1"
                >
                  Keep Transaction
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmCancel}
                  className="flex-1"
                >
                  Cancel Transaction
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingTransactions;